import React from 'react';
import { motion } from 'framer-motion';
import { useScrollReveal, fadeUpVariants, staggerContainerVariants } from './useScrollReveal';

const benefits = [
  {
    num: '01',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" className="w-8 h-8" stroke="#C8F135" strokeWidth="1.5">
        <path d="M20 4l4 8 8 1-6 6 1.5 9L20 24l-7.5 4 1.5-9-6-6 8-1z" />
      </svg>
    ),
    title: 'Innovative Approach',
    desc: 'We craft unique digital experiences that differentiate your brand — no cookie-cutter templates, ever.',
    color: 'from-[#0d1f06] to-[#080808]',
  },
  {
    num: '02',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" className="w-8 h-8" stroke="#C8F135" strokeWidth="1.5">
        <circle cx="20" cy="20" r="12" />
        <path d="M14 20l4 4 8-8" />
      </svg>
    ),
    title: 'Seamless Experience',
    desc: 'A polished user experience across every device, ensuring every touchpoint converts visitors into loyal customers.',
    color: 'from-[#061020] to-[#080808]',
  },
  {
    num: '03',
    icon: (
      <svg viewBox="0 0 40 40" fill="none" className="w-8 h-8" stroke="#C8F135" strokeWidth="1.5">
        <path d="M8 32l8-8 6 6 10-14" />
        <circle cx="32" cy="10" r="2" fill="#C8F135" />
      </svg>
    ),
    title: 'Results Driven',
    desc: "We're not just a vendor — we're your long-term growth partner, offering support long after the project goes live.",
    color: 'from-[#140820] to-[#080808]',
  },
];

const WhyUs: React.FC = () => {
  const { ref, controls } = useScrollReveal();

  return (
    <section id="why-us" className="py-32 bg-[#080808] border-t border-[#0f0f0f]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-10 mb-20">
          <motion.div
            ref={ref}
            initial="hidden"
            animate={controls}
            variants={fadeUpVariants}
          >
            <p className="section-label mb-5">Why Choose Us</p>
            <h2 className="font-serif text-[clamp(2.2rem,5vw,4.5rem)] text-white leading-[1.05] max-w-xl">
              Experience the Benefits of{' '}
              <span className="italic text-lime">Our Expertise</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="font-dm text-[#999] text-sm max-w-xs leading-relaxed lg:text-right"
          >
            Driving impactful results and powerful gains for small businesses nationwide.
          </motion.p>
        </div>

        {/* Cards */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-80px' }}
          variants={staggerContainerVariants}
          className="grid grid-cols-1 md:grid-cols-3 gap-4"
        >
          {benefits.map((b) => (
            <motion.div
              key={b.title}
              variants={fadeUpVariants}
              className="group relative border border-[#141414] rounded-2xl overflow-hidden cursor-default"
              whileHover={{ y: -6, borderColor: '#C8F13520' }}
              transition={{ duration: 0.3 }}
            >
              {/* Gradient bg */}
              <div className={`absolute inset-0 bg-gradient-to-br ${b.color} opacity-60 group-hover:opacity-100 transition-opacity duration-500`} />
              
              <div className="relative z-10 p-8">
                {/* Top row */}
                <div className="flex items-start justify-between mb-8">
                  <div className="w-14 h-14 rounded-xl border border-[#1e1e1e] bg-[#0a0a0a] flex items-center justify-center group-hover:border-lime/20 transition-colors duration-300">
                    {b.icon}
                  </div>
                  <span className="font-syne text-xs font-bold text-[#666] group-hover:text-[#999] transition-colors">{b.num}</span>
                </div>

                <h3 className="font-syne font-bold text-white text-xl mb-3 group-hover:text-lime transition-colors duration-300">{b.title}</h3>
                <p className="font-dm text-[#999] text-sm leading-relaxed group-hover:text-[#bbb] transition-colors duration-300">{b.desc}</p>

                {/* Arrow */}
                <div className="mt-8 flex items-center gap-2 text-[#777] group-hover:text-lime transition-colors duration-300">
                  <span className="font-syne text-xs uppercase tracking-widest">Learn More</span>
                  <svg className="w-3 h-3 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </div>
              </div>
            </motion.div>
          ))}
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          className="mt-8 flex justify-end"
        >
          <a href="#services" className="btn-outline px-6 py-3 text-sm">
            View Services →
          </a>
        </motion.div>
      </div>
    </section>
  );
};

export default WhyUs;
